import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from 'tss-react/mui';
import { Modal, Fade, Backdrop } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Slider from "@mui/material/Slider";
import CircularProgress from "@mui/material/CircularProgress";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Replay5Icon from "@mui/icons-material/Replay5";
import Forward5Icon from "@mui/icons-material/Forward5";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import VoicemailIcon from "@mui/icons-material/Voicemail";
import PropTypes from "prop-types";
import { formatUsPhone } from "../utils/contacts";
import { formatWhen } from "../utils/format";

const SKIP_SECONDS = 5;
const SPEEDS = [1, 1.25, 1.5, 2, 0.75];

const useStyles = makeStyles()((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  paper: {
    position: "relative",
    width: "100%",
    maxWidth: 440,
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    boxShadow: theme.shadows[8],
    outline: "none",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 1.5, 1.5, 2.5),
  },
  badge: {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
  },
  heading: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 17,
    fontWeight: 500,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  subtitle: {
    fontSize: 13,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  close: {
    alignSelf: "flex-start",
    color: theme.palette.text.secondary,
  },
  timeline: {
    padding: theme.spacing(0.5, 3, 0),
  },
  slider: {
    color: theme.palette.primary.light,
    padding: "10px 0",
    "& .MuiSlider-thumb": {
      width: 14,
      height: 14,
    },
    "& .MuiSlider-rail": {
      opacity: 0.3,
    },
  },
  times: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  message: {
    padding: theme.spacing(0, 3),
    fontSize: 13,
    lineHeight: 1.4,
    color: theme.palette.secondary.main,
  },
  controls: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    padding: theme.spacing(1, 1.5, 1.5),
  },
  side: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  sideEnd: {
    justifyContent: "flex-end",
    paddingRight: theme.spacing(1),
  },
  transport: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  control: {
    color: theme.palette.text.secondary,
  },
  play: {
    width: 56,
    height: 56,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
    boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.2)",
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      filter: "brightness(0.95)",
    },
    "&.Mui-disabled": {
      color: "#fff",
      backgroundColor: theme.palette.primary.light,
      opacity: 0.6,
    },
  },
  speed: {
    minWidth: 48,
    padding: theme.spacing(0.25, 1),
    marginLeft: theme.spacing(1),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: 12.5,
    fontWeight: 500,
    textTransform: "none",
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  speedChanged: {
    color: theme.palette.primary.light,
    borderColor: theme.palette.primary.light,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1, 0.75, 2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  hint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("sm")]: {
      visibility: "hidden",
    },
  },
  download: {
    textTransform: "none",
    fontSize: 13,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
  volumeSlider: {
    width: 72,
    marginLeft: theme.spacing(0.5),
    color: theme.palette.primary.light,
    "& .MuiSlider-thumb": {
      width: 12,
      height: 12,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

// 0:09, 1:05, 1:02:07. "--:--" until the length is known.
export const formatClock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "--:--";
  const total = Math.floor(seconds);
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, "0");
  return hrs > 0 ? `${hrs}:${String(mins).padStart(2, "0")}:${secs}` : `${mins}:${secs}`;
};

// Title and subtitle from the voicemail or recording the audio belongs to.
const describe = (from) => {
  if (!from) return { title: "Recording", subtitle: "", voicemail: false, fileName: "recording.wav" };
  const voicemail = Object.prototype.hasOwnProperty.call(from, "vid");
  const kind = voicemail ? "Voicemail" : "Call recording";
  const number = from.number ? String(formatUsPhone(from.number)) : "";
  let title;
  if (from.name) {
    title = from.name;
  } else if (number) {
    title = number;
  } else if (from.title) {
    title = String(from.title).split(".")[0];
  } else {
    title = kind;
  }
  const date = formatWhen(from.dateTime || from.date);
  const subtitle = [title === kind ? "" : kind, from.name && number ? number : "", date].filter(Boolean).join(" · ");
  // Name of the saved file: the recording's own title when it has one.
  const base = String(from.title || "").trim() || title;
  const fileName = /\.[a-z0-9]{2,4}$/i.test(base) ? base : `${base}.wav`;
  return { title, subtitle, voicemail, fileName };
};

const AudioPlayer = (props) => {
    const { classes, cx } = useStyles();
    const { open, setOpen, src, from } = props;
    const player = useRef(null);
    // Starts by itself once: the Play action in the list was the request.
    const autoStarted = useRef(false);

    const [playing, setPlaying] = useState(false);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(NaN);
    const [speed, setSpeed] = useState(1);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);

    // A new recording starts from a clean state.
    useEffect(() => {
      setPlaying(false);
      setReady(false);
      setFailed(false);
      setPosition(0);
      setDuration(NaN);
      setSpeed(1);
      autoStarted.current = false;
    }, [src, open]);

    const audio = () => player.current;

    const handleClose = () => {
      if (audio()) audio().pause();
      setOpen(false);
    };

    const togglePlay = useCallback(() => {
      const el = player.current;
      if (!el) return;
      if (el.paused) {
        const started = el.play();
        if (started && typeof started.catch === "function") {
          started.catch(() => setFailed(true));
        }
      } else {
        el.pause();
      }
    }, []);

    const seekTo = (seconds) => {
      const el = audio();
      if (!el) return;
      const end = Number.isFinite(el.duration) ? el.duration : seconds;
      el.currentTime = Math.min(Math.max(seconds, 0), end);
      setPosition(el.currentTime);
    };

    const skip = (delta) => {
      if (audio()) seekTo(audio().currentTime + delta);
    };

    const cycleSpeed = () => {
      const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
      setSpeed(next);
      if (audio()) audio().playbackRate = next;
    };

    const handleVolume = (event, value) => {
      const level = value / 100;
      setVolume(level);
      setMuted(level === 0);
      if (audio()) {
        audio().volume = level;
        audio().muted = level === 0;
      }
    };

    const toggleMute = () => {
      const next = !muted;
      setMuted(next);
      if (audio()) {
        audio().muted = next;
        // Unmuting from a zero level would stay silent.
        if (!next && audio().volume === 0) {
          audio().volume = 0.5;
          setVolume(0.5);
        }
      }
    };

    const handleLength = () => {
      const el = audio();
      if (el && Number.isFinite(el.duration)) setDuration(el.duration);
    };

    const handleReady = () => {
      setReady(true);
      handleLength();
      const el = audio();
      if (el && !autoStarted.current) {
        autoStarted.current = true;
        // A browser may refuse to start sound by itself; the play button
        // is still there, so that is not an error.
        const started = el.play();
        if (started && typeof started.catch === "function") {
          started.catch(() => {});
        }
      }
    };

    // Space plays and pauses; the arrow keys skip, unless a slider has them.
    const handleKeyDown = (event) => {
      const tag = event.target && event.target.tagName;
      if (tag === "INPUT") return;
      if (event.key === " " && tag !== "BUTTON" && tag !== "A") {
        event.preventDefault();
        togglePlay();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        skip(-SKIP_SECONDS);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        skip(SKIP_SECONDS);
      }
    };

    const { title, subtitle, voicemail, fileName } = describe(from);
    const knownLength = Number.isFinite(duration);
    const VolumeIcon = muted || volume === 0 ? VolumeOffIcon : volume < 0.5 ? VolumeDownIcon : VolumeUpIcon;

    return (
      <Modal
        aria-labelledby="audio-player-title"
        className={classes.modal}
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{
          backdrop: Backdrop
        }}
        slotProps={{
          backdrop: {
            timeout: 300,
          }
        }}
      >
        <Fade in={open}>
          <div className={classes.paper} onKeyDown={handleKeyDown} tabIndex={-1}>
            <div className={classes.header}>
              <div className={classes.badge}>
                {voicemail ? <VoicemailIcon /> : <GraphicEqIcon />}
              </div>
              <div className={classes.heading}>
                <div id="audio-player-title" className={classes.title}>{title}</div>
                {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
              </div>
              <Tooltip title="Close">
                <IconButton aria-label="close" className={classes.close} onClick={handleClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>

            <audio
              ref={player}
              src={src}
              preload="metadata"
              onLoadedMetadata={handleLength}
              onDurationChange={handleLength}
              onCanPlay={handleReady}
              onTimeUpdate={() => audio() && setPosition(audio().currentTime)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => { setPlaying(false); seekTo(0); }}
              onError={() => { setFailed(true); setReady(false); }}
            />

            <div className={classes.timeline}>
              <Slider
                size="small"
                aria-label="position"
                className={classes.slider}
                value={knownLength ? Math.min(position, duration) : 0}
                min={0}
                max={knownLength ? duration : 1}
                step={0.1}
                disabled={!knownLength || failed}
                onChange={(event, value) => seekTo(value)}
                getAriaValueText={formatClock}
              />
              <div className={classes.times}>
                <span>{formatClock(position)}</span>
                <span>{formatClock(duration)}</span>
              </div>
            </div>

            {failed && (
              <div className={classes.message} role="alert">
                This recording could not be played.
              </div>
            )}

            <div className={classes.controls}>
              <div className={classes.side}>
                <Tooltip title="Playback speed">
                  <Button aria-label={`playback speed ${speed}x`} className={cx(classes.speed, speed !== 1 && classes.speedChanged)} onClick={cycleSpeed}>
                    {speed}×
                  </Button>
                </Tooltip>
              </div>

              <div className={classes.transport}>
                <Tooltip title={`Back ${SKIP_SECONDS} seconds`}>
                  <span>
                    <IconButton aria-label="rewind" className={classes.control} onClick={() => skip(-SKIP_SECONDS)} disabled={!ready} size="large">
                      <Replay5Icon />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton
                  aria-label={playing ? "pause" : "play"}
                  className={classes.play}
                  onClick={togglePlay}
                  disabled={!ready || failed}
                  size="large">
                  {!ready && !failed
                    ? <CircularProgress size={24} color="inherit" />
                    : playing ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />}
                </IconButton>
                <Tooltip title={`Forward ${SKIP_SECONDS} seconds`}>
                  <span>
                    <IconButton aria-label="fast forward" className={classes.control} onClick={() => skip(SKIP_SECONDS)} disabled={!ready} size="large">
                      <Forward5Icon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>

              <div className={cx(classes.side, classes.sideEnd)}>
                <Tooltip title={muted ? "Unmute" : "Mute"}>
                  <IconButton aria-label={muted ? "unmute" : "mute"} className={classes.control} onClick={toggleMute} size="small">
                    <VolumeIcon />
                  </IconButton>
                </Tooltip>
                <Slider
                  size="small"
                  aria-label="volume"
                  className={classes.volumeSlider}
                  value={muted ? 0 : Math.round(volume * 100)}
                  min={0}
                  max={100}
                  onChange={handleVolume}
                />
              </div>
            </div>

            <div className={classes.footer}>
              <span className={classes.hint}>Space to play or pause · ← → to skip</span>
              <Button
                component="a"
                href={src || undefined}
                download={fileName}
                disabled={!src || failed}
                size="small"
                startIcon={<DownloadIcon fontSize="small" />}
                className={classes.download}
              >
                Download
              </Button>
            </div>
          </div>
        </Fade>
      </Modal>
    );
}

AudioPlayer.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  src: PropTypes.string,
  from: PropTypes.object,
};

export default AudioPlayer;
